import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva } from "class-variance-authority";
import { X } from "lucide-react"

import { cn } from "../../lib/utils"
import { isCommunicationTarget } from "../../lib/overlayPortal"

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref} />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  "fixed z-50 max-h-[100dvh] overflow-y-auto overscroll-contain gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
)

/**
 * 手机侧边详情使用满宽，不沿用桌面 440px/75% 宽度；上下弹层仍保留调用方高度。
 * 安全区作为透明边界预留，避免强加 padding 破坏 TM 等 p-0 / flex 布局。
 * 关闭按钮不占网格/弹性布局，补偿自身滚动后始终可见，且保持在 Radix 的焦点树中。
 */
const SheetContent = React.forwardRef(({ side = "right", className, children, onScroll, onInteractOutside, ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content ref={ref} className={cn(sheetVariants({ side }), className,
      "max-md:!w-full max-md:!max-w-full max-md:[border-top-width:env(safe-area-inset-top)] max-md:[border-bottom-width:env(safe-area-inset-bottom)] max-md:border-y-transparent",
      (side === "left" || side === "right") && "max-md:h-[100dvh] max-md:max-h-[100dvh]"
    )} {...props}
      onInteractOutside={(event) => {
        // 手机抽屉内打开沟通不丢弃当前编辑内容。
        if (isCommunicationTarget(event.target)) event.preventDefault();
        onInteractOutside?.(event);
      }}
      onScroll={(event) => {
        if (event.target === event.currentTarget) {
          event.currentTarget.style.setProperty("--sheet-scroll-y", `${Math.max(0, event.currentTarget.scrollTop)}px`);
        }
        onScroll?.(event);
      }}>
      <SheetPrimitive.Close
        data-sheet-close=""
        style={{ transform: "translateY(var(--sheet-scroll-y, 0px))" }}
        className="absolute right-1 top-1 z-50 flex h-11 w-11 items-center justify-center rounded-full bg-background/95 text-muted-foreground shadow-sm hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none md:right-2 md:top-2">
        <X className="h-5 w-5" aria-hidden="true" />
        <span className="sr-only">Close</span>
      </SheetPrimitive.Close>
      {children}
    </SheetPrimitive.Content>
  </SheetPortal>
))
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-2 text-center sm:text-left", className, "pr-12")}
    {...props} />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props} />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
